import { cn } from '../lib/cn';
import { initials as initialsFor, personColor } from '../lib/personColor';

/**
 * Avatar — initial-only or photo. Hover shows the full name as tooltip.
 *
 * v3 (design language §5.8): per-person color, 2px outline, photo override.
 * The photo overrides identity when present.
 *
 * Sizes: 'sm' = 24px, 'md' = 32px, or any numeric px.
 */
export interface AvatarProps {
  name: string | null | undefined;
  /** 'sm' (24px), 'md' (32px), or numeric px. */
  size?: 'sm' | 'md' | number;
  /** When provided, render the photo. Initials shown when omitted/null. */
  imageUrl?: string | null;
  /** Outline (boxShadow ring) color. Defaults to surface for clean stacking. */
  outline?: string;
  className?: string;
}

export function Avatar({
  name,
  size = 'sm',
  imageUrl,
  outline = 'var(--paper-1)',
  className,
}: AvatarProps) {
  const numericSize = typeof size === 'number' ? size : size === 'md' ? 32 : 24;
  const px = `${numericSize}px`;
  const fontSize =
    numericSize <= 18
      ? '8.5px'
      : numericSize <= 22
        ? '9.5px'
        : numericSize <= 28
          ? '10px'
          : '11.5px';
  const hasName = !!name?.trim();

  // v3 follow-up #3 (Item 2) — personColor() returns BOTH a class
  // (for founders, themable via CSS vars) AND an inline-style payload
  // (for hash-fallback hues that Tailwind JIT can't compile statically).
  const colorTokens = personColor(name);
  const colorClass = imageUrl
    ? 'bg-paper-3'
    : hasName
      ? colorTokens.className || 'text-white' // empty class → fall through to inline style; keep text white
      : 'bg-paper-2 text-ink-2';
  const inlineColor = !imageUrl && hasName ? colorTokens.style : {};

  return (
    <span
      className={cn(
        'relative inline-grid place-items-center overflow-hidden rounded-token-pill text-white font-semibold leading-none flex-shrink-0 select-none',
        colorClass,
        className,
      )}
      style={{
        width: px,
        height: px,
        fontSize,
        boxShadow: `0 0 0 2px ${outline}`,
        ...inlineColor,
      }}
      data-name={name ?? undefined}
      title={name ?? undefined}
      aria-label={name ?? undefined}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name ?? ''} className="h-full w-full object-cover" />
      ) : hasName ? (
        initialsFor(name)
      ) : (
        '—'
      )}
    </span>
  );
}
